import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col } from 'react-bootstrap';
import { FaCalendarAlt, FaArrowRight, FaBuilding, FaShieldAlt, FaMicrochip, FaLeaf, FaBrain, FaUsers } from 'react-icons/fa';

const newsItems = [
  {
    id: 'rui-gomes-cto',
    slug: 'rui-gomes-appointed-chief-technology-officer',
    title: '6G AI Sweden AB Appoints Rui Gomes as Chief Technology Officer',
    date: 'March 10, 2026',
    author: '6G AI Sweden AB',
    category: 'Press Release',
    excerpt: 'New leadership bolsters the company\'s mission to deliver trusted, sovereign high-performance cloud infrastructure for AI innovation across Europe.',
    image: '/RuiGomes_Headshot.jpg',
    content: `Stockholm, Sweden | 10 March 2026 – 6G AI Sweden AB (6G AI), the leading Nordic AI cloud provider, today announced the appointment of Rui Gomes as Chief Technology Officer (CTO). Gomes will lead 6G AI's platform strategy, engineering execution, and infrastructure development as the company accelerates the development of Sweden's emerging sovereign AI cloud infrastructure and prepares to scale its GPU-as-a-Service (GPUaaS) platform across Europe.

The announcement comes as global demand for AI compute continues to surge and European organizations increasingly seek infrastructure that ensures data sovereignty, regulatory compliance, and trusted AI deployment within national jurisdictions.

Gomes will lead 6G AI's platform architecture, engineering strategy and infrastructure development. His appointment will support the company's next phase of growth alongside its mission to bring cutting-edge AI technology to secure cloud environments with seamless AI accessibility, enterprise-grade reliability, and robust performance for organizations deploying advanced workloads.

6G AI's platform addresses the growing demand for large-scale AI training, inference and high-performance workloads, operating under strict data sovereignty laws to ensure AI workloads remain within European jurisdiction and fully compliant with regional regulatory frameworks.

"AI infrastructure has to be trusted before it can be transformative," said Gomes. "Our focus is to build a purpose-built, sovereign AI cloud infrastructure for enterprises, research institutions, and regulated industries – one that customers can adopt quickly, operate confidently, and scale globally while maintaining full control over their data and workloads."

By combining sovereign cloud architecture with next-generation AI hardware, 6G AI aims to enable organizations to adopt advanced AI capabilities without compromising data governance or regulatory compliance.

**Strengthening Europe's Sovereign AI Infrastructure**

Under Gomes' leadership, 6G AI will accelerate the development of a platform-first sovereign AI cloud architecture, delivering:

- Rapid deployment of AI workloads through operational excellence, transparent scalability and a customer first approach
- Alignment with Swedish jurisdiction and European compliance requirements, ensuring organizations maintain full control over data and AI workloads
- Reliable performance by design across the entire tech stack, from compute infrastructure to workload orchestration

M. A. Zaman, Founder and Group CEO of 6G AI Sweden AB, said the appointment reflects the growing importance of operational excellence in building trusted, national-scale AI infrastructure.

"Our strategy is to build and operate sovereign AI infrastructure as a critical national capability," he stated. "Strengthening our operational leadership is therefore as important as expanding compute capacity. The appointment of Rui Gomes as CTO marks an important step in scaling our HPC environments and operations in Sweden with consistent, high-quality delivery."

**About Rui Gomes**

Rui Gomes brings extensive experience in high-performance computing and digital infrastructure, having previously held senior leadership roles at atNorth, where he led HPC infrastructure operations and product development in large-scale data centre environments.

His expertise across telecom, media and technology sectors will support 6G AI's mission to deliver secure, high-performance AI infrastructure built specifically for European innovation. The appointment comes as organizations across Europe increase investment in sovereign digital infrastructure to support the next wave of AI-driven innovation.

**About 6G AI**

6G AI Sweden AB is a Swedish AI cloud provider offering sovereign GPU compute designed for easy AI adoption and predictable pricing. The company provides high-performance AI infrastructure powered by NVIDIA GPU technology. Operating from atNorth's SWE01 data center in Kista, Stockholm, the platform capitalizes on sustainability capabilities to help reduce the environmental footprint of large-scale AI workloads.

Hosted in Sweden, 6G AI's next-generation cloud infrastructure is designed to accelerate enterprise AI adoption while ensuring compliance with European data sovereignty requirements for industries including research, healthcare, telecommunications, and public sector innovation.`,
    features: [
      { icon: FaMicrochip, text: 'NVIDIA GPU Technology' },
      { icon: FaShieldAlt, text: 'Data Sovereignty' },
      { icon: FaLeaf, text: 'Sustainable Infrastructure' },
    ]
  },
  {
    id: 'sovereign-ai-society',
    slug: 'sovereign-ai--societal-impact-and-sweden-s-competitiveness',
    title: 'Sovereign AI, Societal impact and Sweden\'s competitiveness',
    date: 'January 10, 2026',
    author: '6G AI Sweden AB',
    category: 'Event',
    excerpt: 'Join us for a landmark event exploring secure AI and sustainable innovation in Sweden. The society as a whole needs to collaborate in the areas of data-driven insights and decisions.',
    content: `The path towards secure AI and sustainable innovation

Time: 10:00–12:30  
Date: 16 January 2026  
Location: Grant Thornton, Kungsgatan 57, Stockholm

Our society faces a number of complex challenges; polarization, social exclusion, geopolitical conflicts, mental health and climate change. The society as a whole needs to collaborate in the areas of data-driven insights and decisions, defining the way forward for Sweden and its citizens.

Maintaining competitiveness requires more than technology - we must make our societal value visible and develop Swedish sovereign AI with full data control, in secure Swedish or European data centers, governed by our ethical principles.

This is as important as energy supply - a prerequisite for research, innovation and a sustainable, competitive society.

Program and speakers:

How & why has Swedish Hockey League measured its impact, how can the method drive change in society?
- Helena Mueller, Director Sustainability Consulting Grant Thornton
- Johan Cahling, Head of Sustainable Development Swedish Hockey League

Sweden at a crossroads: Competitiveness with the help of superior AI
- Thomas Ekman, 6G AI Board Member, former CEO of the Axel Johnson Group, CEO Dustin, CEO Tele2 Sweden

Protecting lives, promoting research and strengthening trust with Swedish AI
- Elisabet Dahlin, Chairwoman of the Swedish Childhood Cancer Foundation

Sovereign AI and global power dynamics – what Sweden must prepare for
- MA Zaman, Chairman & Group CEO 6G AI SWEDEN AB`,
    features: [
      { icon: FaCalendarAlt, text: 'January 16, 2026' },
      { icon: FaBuilding, text: 'Grant Thornton, Stockholm' },
    ]
  },
  {
    id: 'launch-sovereign-ai',
    slug: '6g-ai-sweden-ab-launches-sovereign-ai-infrastructure-in-sweden',
    title: '6G AI Sweden AB launches Sovereign AI Infrastructure in Sweden',
    date: 'January 9, 2026',
    author: '6G AI Sweden AB',
    category: 'Press Release',
    excerpt: '6G AI Sweden AB announces commercial launch of sovereign AI infrastructure in Sweden, positioning Sweden at the forefront of artificial intelligence with complete data confinement within national borders.',
    content: `STOCKHOLM, Sweden, 2nd July, 2025 - 6G AI Sweden AB today announced the commercial launch of new sovereign AI Infrastructure in Sweden, a transformative technology project that positions Sweden at the forefront of artificial intelligence with the ability to provide complete data confinement within national borders.

The AI Cloud facility is strategically located in atNorth's SWE01 datacenter in Stockholm's Kista technology district. It is fully aligned with the NVIDIA reference architecture for optimal performance and delivers direct access to NVIDIA H200 GPUs. 6G AI Sweden AB has previously signed an agreement to purchase Nvidia GPUs at a total value of SEK 2.1 billion.

6G AI Sweden is leveraging Nvidia AI Enterprise suite, Nvidia AI Blueprints, Use Cases and pre-trained models and is providing Swedish organizations with computational capabilities previously accessible only through foreign-controlled infrastructure.

Key Highlights:
- Fully aligned with NVIDIA reference architecture
- Direct access to NVIDIA H200 GPUs
- SEK 2.1 billion GPU investment
- Located in atNorth's SWE01 datacenter, Kista
- Full GDPR and EU AI Act compliance
- 100% renewable energy with heat recovery

The initiative has attracted distinguished industry leaders to its board, including Johnny Svedberg with over three decades of telecommunications experience.`,
    features: [
      { icon: FaShieldAlt, text: 'GDPR & EU AI Act Compliant' },
      { icon: FaMicrochip, text: 'NVIDIA H200 GPUs' },
      { icon: FaLeaf, text: '100% Renewable Energy' },
      { icon: FaBrain, text: 'NVIDIA AI Enterprise' },
    ]
  }
];

function News() {
  return (
    <>
      <Helmet>
        <title>News | 6G AI Sweden AB</title>
        <meta name="description" content="Latest news and updates from 6G AI Sweden AB - Sovereign AI infrastructure, events, and industry insights." />
        <meta property="og:title" content="News | 6G AI Sweden AB" />
        <meta property="og:description" content="Latest news and updates from 6G AI Sweden AB" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="page-header">
        <Container>
          <Row className="align-items-center">
            <Col lg={8}>
              <h1 className="page-title">News</h1>
              <p className="page-subtitle">
                Stay updated with the latest developments in sovereign AI infrastructure and AI innovation in Sweden.
              </p>
            </Col>
          </Row>
        </Container>
      </div>

      <section className="section">
        <Container>
          <Row>
            {newsItems.map((item, index) => (
              <Col lg={6} key={item.id} className="mb-4">
                <article className="news-card interactive-card">
                  {item.image && (
                    <div className="news-card-image">
                      <img src={item.image} alt={item.title} style={{ width: '25%', borderRadius: '8px', marginBottom: '1rem' }} />
                    </div>
                  )}
                  <div className="news-card-header">
                    <span className="news-category">{item.category}</span>
                    <span className="news-date">
                      <FaCalendarAlt /> {item.date}
                    </span>
                  </div>
                  <h2 className="news-title">{item.title}</h2>
                  <p className="news-excerpt">{item.excerpt}</p>
                  <div className="news-features">
                    {item.features.map((feature, idx) => (
                      <div key={idx} className="news-feature">
                        <feature.icon className="news-feature-icon" />
                        <span>{feature.text}</span>
                      </div>
                    ))}
                  </div>
                  <Link to={`/news/${item.slug}`} className="news-link">
                    Read More <FaArrowRight />
                  </Link>
                </article>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <style>{`
        .news-card {
          background: var(--md-sys-color-surface-container);
          border: 1px solid var(--md-sys-color-outline-variant);
          border-radius: var(--radius-extra-large);
          padding: var(--space-xl);
          height: 100%;
          display: flex;
          flex-direction: column;
          transition: all var(--motion-duration-medium4) var(--motion-easing-emphasized);
        }

        .news-card:hover {
          transform: translateY(-4px) scale(1.01);
          box-shadow: var(--md-sys-elevation-4), 0 0 30px rgba(199, 125, 255, 0.1);
          border-color: var(--md-sys-color-primary-container);
        }

        .news-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--space-md);
          flex-wrap: wrap;
          gap: var(--space-sm);
        }

        .news-category {
          background: var(--md-sys-color-primary-container);
          color: var(--md-sys-color-on-primary-container);
          padding: var(--space-xs) var(--space-sm);
          border-radius: var(--radius-full);
          font-size: var(--font-label-md);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .news-date {
          display: flex;
          align-items: center;
          gap: var(--space-xs);
          color: var(--md-sys-color-on-surface-variant);
          font-size: var(--font-body-sm);
        }

        .news-date svg {
          color: var(--md-sys-color-secondary);
        }

        .news-title {
          font-size: var(--font-headline-sm);
          font-weight: 700;
          margin-bottom: var(--space-md);
          line-height: 1.3;
          color: var(--md-sys-color-on-surface);
        }

        .news-excerpt {
          color: var(--md-sys-color-on-surface-variant);
          font-size: var(--font-body-md);
          line-height: 1.7;
          margin-bottom: var(--space-lg);
          flex-grow: 1;
        }

        .news-features {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-md);
          margin-bottom: var(--space-lg);
          padding-top: var(--space-md);
          border-top: 1px solid var(--md-sys-color-outline-variant);
        }

        .news-feature {
          display: flex;
          align-items: center;
          gap: var(--space-xs);
          color: var(--md-sys-color-on-surface-variant);
          font-size: var(--font-body-sm);
        }

        .news-feature-icon {
          color: var(--md-sys-color-tertiary);
          font-size: 0.875rem;
        }

        .news-link {
          display: inline-flex;
          align-items: center;
          gap: var(--space-sm);
          color: var(--md-sys-color-primary);
          font-weight: 600;
          font-size: var(--font-body-md);
          transition: all var(--motion-duration-medium2) var(--motion-easing-emphasized);
        }

        .news-link:hover {
          color: var(--md-sys-color-secondary);
          gap: var(--space-md);
        }

        .news-link svg {
          transition: transform var(--motion-duration-medium2) var(--motion-easing-emphasized);
        }

        .news-link:hover svg {
          transform: translateX(4px);
        }
      `}</style>
    </>
  );
}

export default News;
